import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/** Messages écrits entre collègues de la même entreprise (tous les plans).
 * Rien ne passe par Twilio : la base et le canal SSE de l'entreprise. */

export interface PieceJointeCollegue {
  url: string
  content_type: string
  filename: string
}

export interface MessageCollegue {
  kind: 'message'
  id: number
  from_user_id: number
  to_user_id: number
  text: string
  attachment: PieceJointeCollegue | null
  created_at: string
  read_at: string | null
}

/** Un appel interne entre les deux personnes, mêlé aux messages par date. */
export interface AppelCollegue {
  kind: 'call'
  id: number
  from_user_id: number
  to_user_id: number
  status: string
  /** L'appelé n'a pas décroché (ou était hors ligne, occupé). */
  missed: boolean
  duration: number
  created_at: string
}

export type ElementFilCollegue = MessageCollegue | AppelCollegue

/** Un fil par collègue : le dernier message et les non lus. */
export interface FilCollegue {
  user_id: number
  name: string
  last: { text: string; has_attachment: boolean; mine: boolean; at: string } | null
  unread: number
}

interface PageMessages {
  items: ElementFilCollegue[]
  has_more: boolean
  /** Curseur de la page d'avant (date du plus ancien élément affiché). */
  next_before: string | null
}

export const CLE_MESSAGES_COLLEGUES = ['colleague-messages']
const PAGE = 30

export function useFilsCollegues(enabled = true) {
  return useQuery({
    queryKey: [...CLE_MESSAGES_COLLEGUES, 'fils'],
    queryFn: async () => {
      const { data } = await api.get<{ items: FilCollegue[] }>('/api/colleague-messages/')
      return data.items
    },
    enabled,
  })
}

/** Non lus par collègue (user_id → nombre). */
export function useNonLusCollegues(enabled = true): Record<number, number> {
  const { data = [] } = useFilsCollegues(enabled)
  return Object.fromEntries(data.filter((f) => f.unread > 0).map((f) => [f.user_id, f.unread]))
}

/** Le fil avec un collègue : messages et appels internes. Pages du plus
 * récent au plus ancien ; dans chaque page, du plus ancien au plus récent. */
export function useMessagesCollegue(userId: number) {
  return useInfiniteQuery({
    queryKey: [...CLE_MESSAGES_COLLEGUES, 'fil', userId],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const { data } = await api.get<PageMessages>(`/api/colleague-messages/${userId}/`, {
        params: { limit: PAGE, before: pageParam ?? undefined },
      })
      return data
    },
    initialPageParam: null as string | null,
    getNextPageParam: (derniere) => (derniere.has_more && derniere.next_before ? derniere.next_before : undefined),
  })
}

export function useEnvoyerMessageCollegue(userId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (corps: { text: string; attachment?: { s3_key: string; content_type: string; filename: string } }) => {
      const { data } = await api.post<MessageCollegue>(`/api/colleague-messages/${userId}/`, corps)
      return data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CLE_MESSAGES_COLLEGUES }),
  })
}

export function useMarquerLuCollegue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: number) => {
      await api.post(`/api/colleague-messages/${userId}/read/`)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [...CLE_MESSAGES_COLLEGUES, 'fils'] }),
  })
}

/** « X écrit… » : sans attendre de réponse, une erreur ne dit rien à personne. */
export function signalerFrappeCollegue(userId: number) {
  void api.post(`/api/colleague-messages/${userId}/typing/`).catch(() => undefined)
}
